import type { TransactionDto } from "@home/shared";
import { money, shortDate } from "../format.ts";
import { t, type Lang } from "../i18n.ts";

export function txTitle(tx: TransactionDto): string {
  if (tx.comment) return tx.comment;
  // Перевод между своими счетами — «UZKEK → Farmi» (чужой личный счёт сервер называет «личный счёт»).
  if (tx.type === "transfer") return tx.toAccountName ? `${tx.accountName} → ${tx.toAccountName}` : t("transferL");
  return tx.categoryName ?? "";
}

/**
 * Сумма со знаком: доход «+» (зелёный), расход «−», перевод своим — без знака.
 * perspective — счёт, со стороны которого смотрим (страница счёта): перевод с него — «−»,
 * на него — «+», при обмене валюты — сколько пришло.
 */
export function txAmount(tx: TransactionDto, lang: Lang, perspective?: string): { text: string; income: boolean } {
  if (perspective && tx.type === "transfer") {
    const incoming = tx.accountId !== perspective;
    const amount = incoming ? (tx.toAmount ?? tx.amount) : tx.amount;
    const currency = incoming ? (tx.toCurrency ?? tx.currency) : tx.currency;
    return { text: money(incoming ? amount : -amount, currency, lang, incoming), income: incoming };
  }
  const sign = tx.type === "income" ? 1 : tx.type === "expense" ? -1 : 0;
  return { text: money(sign === 0 ? tx.amount : sign * tx.amount, tx.currency, lang, sign > 0), income: sign > 0 };
}

/** Счёт операции в строке списка: «💳 UZKEK», у перевода — «UZKEK → Farmi». */
function accountMeta(tx: TransactionDto, perspective?: string): string | null {
  if (perspective) {
    // На странице счёта сам счёт не нужен — только вторая сторона перевода.
    if (tx.type !== "transfer") return null;
    return tx.accountId === perspective ? `→ ${tx.toAccountName ?? "?"}` : `← ${tx.accountName}`;
  }
  if (tx.type === "transfer" && tx.toAccountName && tx.toAccountName !== tx.accountName) return `${tx.accountName} → ${tx.toAccountName}`;
  return `💳 ${tx.accountName}`;
}

/** Список операций; с onPick строки нажимаются и открывают подробности. */
export function TxList({
  items,
  lang,
  onPick,
  showDate = true,
  perspective,
}: {
  items: TransactionDto[];
  lang: Lang;
  onPick?: (tx: TransactionDto) => void;
  showDate?: boolean;
  /** Страница счёта: суммы переводов — со знаком относительно этого счёта. */
  perspective?: string;
}) {
  // Счёт в строке — когда операции с разных счетов (иначе он и так понятен).
  const manyAccounts = perspective !== undefined || new Set(items.map((tx) => tx.accountId)).size > 1;
  return (
    <ul className="tx-list">
      {items.map((tx) => {
        const amount = txAmount(tx, lang, perspective);
        // Счёт уже назван в заголовке («зарплата UZKEK», «UZKEK → Farmi») — второй раз не пишем.
        const account = manyAccounts ? accountMeta(tx, perspective) : null;
        const accountShown = account !== null && txTitle(tx).includes(account.replace(/^(💳|←|→) /, ""));
        const meta = [
          showDate ? shortDate(tx.date) : null,
          tx.categoryName && tx.comment ? tx.categoryName : null,
          !tx.mine ? tx.authorName : null,
          accountShown ? null : account,
        ].filter(Boolean);
        const body = (
          <>
            <span className="tx-icon" aria-hidden="true">
              {tx.type === "transfer" ? "🔁" : tx.icon}
            </span>
            <div className="tx-body">
              <div className="tx-title">
                {txTitle(tx)}
                {tx.personal && " 🔒"}
              </div>
              {meta.length > 0 && <div className="tx-meta">{meta.join(" · ")}</div>}
            </div>
            <div className={amount.income ? "tx-amount income" : "tx-amount"}>{amount.text}</div>
          </>
        );
        return (
          <li key={tx.id} className="tx-row">
            {onPick ? (
              <button type="button" className="tx-btn" onClick={() => onPick(tx)}>
                {body}
              </button>
            ) : (
              body
            )}
          </li>
        );
      })}
    </ul>
  );
}
